/**
 * CurriculumSection — Importa currículo (XLSX) y visualiza indicadores por grado.
 *
 * Restringido a Super Admin (v1.4.2). Previamente vivía en InstitutionAdminDashboard;
 * se movió aquí porque solo el super admin debería administrar el currículo global.
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function CurriculumSection() {
  // Import state
  const [curriculumFile, setCurriculumFile] = useState(null);
  const [curriculumSubject, setCurriculumSubject] = useState("comunicacion-lenguaje");
  const [curriculumGrade, setCurriculumGrade] = useState("all");
  const [curriculumImportLoading, setCurriculumImportLoading] = useState(false);
  const [curriculumImportResult, setCurriculumImportResult] = useState(null);

  // View state
  const [curriculumViewSubject, setCurriculumViewSubject] = useState("comunicacion-lenguaje");
  const [curriculumViewGrade, setCurriculumViewGrade] = useState("7");
  const [curriculumData, setCurriculumData] = useState([]);
  const [curriculumViewLoading, setCurriculumViewLoading] = useState(false);

  const runCurriculumImport = async () => {
    if (!curriculumFile) return;
    try {
      setCurriculumImportLoading(true);
      setCurriculumImportResult(null);
      const form = new FormData();
      form.append("file", curriculumFile);
      form.append("subject", curriculumSubject);
      form.append("grade", curriculumGrade);
      const res = await axios.post(`${API}/curriculum/import`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCurriculumImportResult(res.data);
      toast.success("Currículo importado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar currículo");
    } finally {
      setCurriculumImportLoading(false);
    }
  };

  const fetchCurriculum = async () => {
    try {
      setCurriculumViewLoading(true);
      const res = await axios.get(
        `${API}/curriculum/${curriculumViewGrade}/${curriculumViewSubject}/all`,
        { withCredentials: true },
      );
      setCurriculumData(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar currículo");
      setCurriculumData([]);
    } finally {
      setCurriculumViewLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="curriculum-section">
      <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
        <p className="text-sm font-medium text-blue-700 mb-1">Importar currículo (XLSX)</p>
        <p className="text-xs text-blue-600">
          Cada hoja debe representar un grado. La hoja de resumen se ignora automáticamente.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <label className="text-xs text-gray-500 font-medium">Materia</label>
          <Select value={curriculumSubject} onValueChange={setCurriculumSubject}>
            <SelectTrigger data-testid="curriculum-subject-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="comunicacion-lenguaje">Lenguaje y Literatura</SelectItem>
              <SelectItem value="matematica-datos">Matemática</SelectItem>
              <SelectItem value="ciencia-tecnologia">Ciencia y Tecnología</SelectItem>
              <SelectItem value="ciudadania-valores">Ciudadanía y Valores</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-gray-500 font-medium">Grado a importar</label>
          <Select value={curriculumGrade} onValueChange={setCurriculumGrade}>
            <SelectTrigger data-testid="curriculum-grade-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los grados</SelectItem>
              <SelectItem value="4">4°</SelectItem>
              <SelectItem value="5">5°</SelectItem>
              <SelectItem value="6">6°</SelectItem>
              <SelectItem value="7">7°</SelectItem>
              <SelectItem value="8">8°</SelectItem>
              <SelectItem value="9">9°</SelectItem>
              <SelectItem value="1-bach">1° Bachillerato</SelectItem>
              <SelectItem value="2-bach">2° Bachillerato</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-gray-500 font-medium">Archivo XLSX</label>
          <Input
            type="file"
            accept=".xlsx"
            onChange={(e) => setCurriculumFile(e.target.files?.[0] || null)}
            data-testid="curriculum-file-input"
          />
        </div>
      </div>

      <Button
        onClick={runCurriculumImport}
        disabled={curriculumImportLoading || !curriculumFile}
        className="rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white"
        data-testid="curriculum-import-btn"
      >
        {curriculumImportLoading ? "Importando..." : "Importar currículo"}
      </Button>

      {curriculumImportResult && (
        <div className="rounded-xl border border-gray-100 p-4" data-testid="curriculum-import-summary">
          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
            <Badge className="bg-green-50 text-green-700">Insertados: {curriculumImportResult.inserted}</Badge>
            <Badge className="bg-blue-50 text-blue-700">Actualizados: {curriculumImportResult.updated}</Badge>
            <Badge className="bg-gray-100 text-gray-600">Omitidos: {curriculumImportResult.skipped}</Badge>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Hojas procesadas: {(curriculumImportResult.processed_sheets || []).join(", ") || "-"}
          </p>
        </div>
      )}

      <div className="border-t border-gray-100 pt-6">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="space-y-1">
            <label className="text-xs text-gray-500 font-medium">Materia para visualizar</label>
            <Select value={curriculumViewSubject} onValueChange={setCurriculumViewSubject}>
              <SelectTrigger data-testid="curriculum-view-subject"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="comunicacion-lenguaje">Lenguaje y Literatura</SelectItem>
                <SelectItem value="matematica-datos">Matemática</SelectItem>
                <SelectItem value="ciencia-tecnologia">Ciencia y Tecnología</SelectItem>
                <SelectItem value="ciudadania-valores">Ciudadanía y Valores</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-500 font-medium">Grado</label>
            <Select value={curriculumViewGrade} onValueChange={setCurriculumViewGrade}>
              <SelectTrigger data-testid="curriculum-view-grade"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4°</SelectItem>
                <SelectItem value="5">5°</SelectItem>
                <SelectItem value="6">6°</SelectItem>
                <SelectItem value="7">7°</SelectItem>
                <SelectItem value="8">8°</SelectItem>
                <SelectItem value="9">9°</SelectItem>
                <SelectItem value="1-bach">1° Bachillerato</SelectItem>
                <SelectItem value="2-bach">2° Bachillerato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={fetchCurriculum}
            disabled={curriculumViewLoading}
            className="rounded-xl bg-slate-900 text-white"
            data-testid="curriculum-view-btn"
          >
            {curriculumViewLoading ? "Cargando..." : "Cargar currículo"}
          </Button>
        </div>

        <div className="mt-4 rounded-xl border border-gray-100 overflow-hidden">
          <Table data-testid="curriculum-table">
            <TableHeader>
              <TableRow>
                <TableHead>Unidad</TableHead>
                <TableHead>Contenidos</TableHead>
                <TableHead>Código</TableHead>
                <TableHead>Indicador</TableHead>
                <TableHead>Bloom</TableHead>
                <TableHead>Habilidad</TableHead>
                <TableHead>Actividad IA</TableHead>
                <TableHead>Evaluación</TableHead>
                <TableHead>Dificultad</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {curriculumData.map((row, idx) => (
                <TableRow
                  key={`${row.indicator_code || "row"}-${row.unit || ""}-${idx}`}
                  data-testid={`curriculum-row-${idx}`}
                >
                  <TableCell className="font-medium">{row.unit || "-"}</TableCell>
                  <TableCell className="text-xs text-gray-600">{row.contents || "-"}</TableCell>
                  <TableCell>{row.indicator_code || "-"}</TableCell>
                  <TableCell className="text-xs text-gray-700">{row.indicator_text || "-"}</TableCell>
                  <TableCell>{row.bloom_level || "-"}</TableCell>
                  <TableCell>{row.habilidad || "-"}</TableCell>
                  <TableCell>{row.activity_type || "-"}</TableCell>
                  <TableCell>{row.evaluation_type || "-"}</TableCell>
                  <TableCell>{row.difficulty_level || "-"}</TableCell>
                </TableRow>
              ))}
              {curriculumData.length === 0 && !curriculumViewLoading && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-gray-400 py-6">
                    No hay datos para mostrar
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
